import React from 'react';

import { Card, Spin, Tooltip } from 'antd-v5';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { MdCancel, MdCheckCircle, MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Heading, Text } from 'scouting-ui/lib/Text';

import { RenewalStatus } from '@appTypes/enums';
import type {
  CompletePersonProfile,
  PersonProfile,
  PersonRegistrationData,
} from '@appTypes/esb';
import type { Program } from '@shared/constants';
import { luxonViewDateFormat } from '@shared/constants';
import { getPreferredName } from '@shared/utils/preferredName';

import PersonAvatar from '../PersonAvatar';
import ProfileOARank from '../ProfileOARank';
import styles from './YouthProfileCard.module.scss';

export interface YouthProfileCardProps {
  /** The `userId` person profile from Scoutbook for: name, member id, units, programs, ranks. */
  profile?: PersonProfile;
  /**
   * The Akela profile (personGuid). Source of the picture and of the registration status
   */
  akelaProfile?: CompletePersonProfile;
  /** The program the youth is being viewed in. Colors the header. */
  activeProgram?: Program;
  /** The profile request is still in flight. */
  loading?: boolean;
  /** Picked from `akelaProfile.registrarInfo` for the relevant org. */
  registration?: PersonRegistrationData;
  registrationLoading?: boolean;
  /** Slot for the rank line. This card knows nothing about advancement data. */
  lastRank?: React.ReactNode;
  /** Slot for a menu in the top right corner. */
  actions?: React.ReactNode;
  /** Slot for a control laid over the avatar, e.g. a photo upload button. */
  avatarOverlay?: React.ReactNode;
}

interface RegistrationStatusProps {
  registration: PersonRegistrationData;
}

/**
 * The membership pill, e.g. "OPTED OUT (DROPPING)".
 */
function RegistrationStatus(props: RegistrationStatusProps) {
  const { registration } = props;

  const { renewalStatus } = registration;
  const isOptedOut = renewalStatus === RenewalStatus.OptedOut;
  const isCurrent = renewalStatus === RenewalStatus.Current;

  let Icon = MdInfo;
  if (isOptedOut) Icon = MdCancel;
  if (isCurrent) Icon = MdCheckCircle;

  return (
    <Tooltip
      placement="bottom"
      title={
        <FormattedMessage
          id="youthProfileCard.registrationTooltip"
          values={{
            status: registration.registrationStatus,
            effectiveDate: registration.registrationEffectiveDt,
            expireDate: registration.registrationExpireDt,
          }}
        />
      }
    >
      <div className={styles.registration}>
        {renewalStatus}{' '}
        {isOptedOut && <FormattedMessage id="youthProfileCard.dropping" />}
        <Icon
          className={cn(styles.statusIcon, {
            [styles.optedOut]: isOptedOut,
            [styles.current]: isCurrent,
          })}
        />
      </div>
    </Tooltip>
  );
}

export default function YouthProfileCard(props: YouthProfileCardProps) {
  const {
    profile,
    akelaProfile,
    activeProgram,
    loading,
    registration,
    registrationLoading,
    lastRank,
    actions,
    avatarOverlay,
  } = props;

  const person = profile?.profile;
  const units = profile?.organizationPositions ?? [];
  const programs = profile?.currentProgramsAndRanks ?? [];

  return (
    <Card
      className={cn(styles.card, { [styles.flush]: !loading })}
      loading={loading}
    >
      <div
        className={cn(styles.header, activeProgram && styles[activeProgram])}
      >
        {actions && <div className={styles.actions}>{actions}</div>}

        <div className={styles.avatarWrapper}>
          {avatarOverlay && (
            <div className={styles.avatarOverlay}>{avatarOverlay}</div>
          )}
          <PersonAvatar
            size="xl"
            isAdult={false}
            gender={person?.gender}
            pictureUrl={akelaProfile?.profile?.pictureUrl}
          />
        </div>

        <Heading level={2} font="display-h5">
          {person ? getPreferredName(person) : null}
        </Heading>

        {registrationLoading && <Spin size="small" />}
        {!registrationLoading && registration && (
          <RegistrationStatus registration={registration} />
        )}

        {lastRank}

        <div className={styles.field}>
          <Text strong font="body-caption">
            <FormattedMessage id="shared.id" />
          </Text>
          <Text>{person?.memberId}</Text>
        </div>

        {units.map(u => (
          <div className={styles.field} key={u.organizationGuid}>
            <Text strong font="body-caption">
              <FormattedMessage id="youthProfileCard.unit" />
            </Text>
            <Text>{`${u.unitType} ${u.unitNumber}`}</Text>
          </div>
        ))}

        {programs
          .filter(p => !!p.dateStarted)
          .map(p => (
            <div className={styles.field} key={p.programId}>
              <Text>
                <FormattedMessage
                  id="youthProfileCard.dateJoined"
                  values={{ program: p.program }}
                />
              </Text>
              <Text>
                {DateTime.fromISO(p.dateStarted).toFormat(luxonViewDateFormat)}
              </Text>
            </div>
          ))}

        <ProfileOARank profile={person ?? {}} />
      </div>
    </Card>
  );
}
